import { useCallback, useMemo, useRef, useEffect } from 'react'
import { connect } from 'react-redux'
import { onSaveAndActionChange } from '../../actions/buttons'
import MfActionSelect from './MfActionSelect'

/**
 * SaveButton component renders a button with a dropdown of actions.
 *
 * @param {boolean} props.disabled - Whether the button should be disabled.
 * @param {string} props.pos - The position of the button.
 * @param {Function} props.onSaveAndEdit - Callback function for the "Save and Edit" action.
 * @param {Function} props.onSaveAndClose - Callback function for the "Save and Close" action.
 * @returns {JSX.Element} The rendered component.
 */

const SaveButton = props => {
  const selectRef = useRef(null)
  const prevSelectedRef = useRef(props.selectedOption)
  const onOptionChange = useCallback(
    option => {
      if (option.value === props.selectedOption.value) return
      prevSelectedRef.current = option
      props.onSaveAndActionChange(option)
    },
    [props.selectedOption, props.onSaveAndActionChange]
  )

  /**
   * This effect makes sure that the selected option is updated when the selectedOption prop changes
   */
  useEffect(() => {
    if (!selectRef.current) return
    if (prevSelectedRef.current.value !== props.selectedOption.value) {
      prevSelectedRef.current = props.selectedOption
      // This is a hack to update the selected option in the MfActionSelect component. onPress: () => {}
      selectRef.current.setValue({ ...props.selectedOption, manualUpdate: true })
    }
  }, [props.selectedOption])

  const actions = useMemo(
    () =>
      props.options.map(option => ({
        ...option,
        onPress: props[option.onPressKey]
      })),
    [props.options, props.onSaveAndEdit, props.onSaveAndClose]
  )

  const selectedOption = useMemo(
    () => ({ ...props.selectedOption, onPress: props[props.selectedOption.onPressKey] }),
    [props.selectedOption, props.onSaveAndEdit, props.onSaveAndClose]
  )

  const selectProps = {
    getOptionLabel: o => o.label,
    getOptionValue: o => o.value
  }

  return (
    <MfActionSelect
      {...props}
      selectRef={selectRef}
      color="#2AA849"
      selectedOption={selectedOption}
      onOptionChange={onOptionChange}
      actions={actions}
      {...selectProps}
    />
  )
}

const mapStateToProps = state => ({
  options: state.buttons.saveAndAction.options,
  selectedOption: state.buttons.saveAndAction.selectedOption
})

export default connect(mapStateToProps, { onSaveAndActionChange })(SaveButton)
